import React, { useEffect, useState } from 'react'
import { getFormattedDateAndTime, TypeFilter, useTestsCountAnalytics } from '@modmd/data'
import DashboardPage from 'internal-portal/sharedComponents/DashboardPage'
import { Box, Container } from 'components/Layout'
import Card from 'components/Card'
import { List } from 'semantic-ui-react'
import { nanoid } from 'nanoid'
import 'semantic-ui-css/components/list.min.css'
import { useSearchState } from 'internal-portal/utils/useSearchState'
import { DateInput } from 'components/DateInput'
import { Text } from 'components/Typography'
import { format } from 'date-fns'
import { pxToRem, DEVICE, fontSizes, CHART_COLORS } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { Loading } from 'components/Loading'
import { LoadingSkeleton } from 'components/Datalist'
import { StatesDropdown } from 'sharedComponents/StatesDropdown'
import { Label } from 'components/FormFields'
import { Pie, Line } from 'react-chartjs-2'
import _ from 'lodash'
import { SelectInput } from 'components/SelectInput'
import Button from 'components/Button'
import { TYPE_FILTER_OPTIONS } from '../TestSessions/utils'

const SKELETON_ROWS = 5
const SKELETON_COLUMNS = 4

export enum FormName {
  DATE_FROM = 'dateFrom',
  DATE_TO = 'dateTo',
  STATE = 'state',
  TYPE_FILTER = 'typeFilter',
}

interface MapChartLabel {
  [key: string]: string | undefined
}

const CHART_LABEL: MapChartLabel = {
  DeclineToState: 'Decline to state',
  NotHispanic: 'Not hispanic',
  AmericanIndian: 'American Indian',
}

const Analytics: React.FC = () => {
  const searchState = useSearchState<{
    [FormName.DATE_TO]: string
    [FormName.DATE_FROM]: string
    [FormName.STATE]: string
    [FormName.TYPE_FILTER]: TypeFilter
  }>()

  const today = new Date()
  const lastWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7)

  const { dateTo, dateFrom, state, typeFilter } = searchState.data
  const [searchDates, setSearchDates] = useState({
    from: dateFrom ? new Date(dateFrom) : lastWeek,
    to: dateTo ? new Date(dateTo) : today,
  })
  const { current: uuid } = React.useRef(nanoid())
  const [
    getAnalyticsData,
    { data: analytics, loading: isLoadingAnalytics },
  ] = useTestsCountAnalytics()

  const getData = () => {
    getAnalyticsData({
      variables: {
        filterData: {
          testedBetween: [dateFrom, dateTo],
          state,
          typeFilter,
        },
      },
    })
  }

  useEffect(() => {
    void getData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleFilter = () => {
    void getData()
    setSearchDates({
      from: dateFrom ? new Date(dateFrom) : lastWeek,
      to: dateTo ? new Date(dateTo) : today,
    })
  }

  const isMobile = !useIsMinDevice(DEVICE.TABLET)

  const AnalyticsList = (props: { header: string; count: string }) => {
    const { header, count } = props
    return (
      <List.Item>
        <List.Content floated="right">
          <List horizontal>
            <List.Item>{count}</List.Item>
            <List.Item>
              {((Number(count) / analytics!.testsCountAnalytics.total!) * 100).toFixed(2)}%
            </List.Item>
          </List>
        </List.Content>
        <List.Header>{header}</List.Header>
      </List.Item>
    )
  }

  const getChartLabel = (value: string | undefined | null) => value && (CHART_LABEL[value] ?? value)

  const genderChartData = {
    labels: analytics?.testsCountAnalytics.genders
      ? (Object.keys(analytics.testsCountAnalytics.genders)
          .map((key) => getChartLabel(key))
          .slice(1) as string[])
      : [],
    datasets: [
      {
        label: 'Rainfall',
        backgroundColor: CHART_COLORS,
        data: analytics?.testsCountAnalytics.genders
          ? Object.values(analytics.testsCountAnalytics.genders)
              .map((key) => (key ? String(key) : '0'))
              .slice(1)
          : [],
      },
    ],
  }

  const ethnicityChartData = {
    labels: analytics?.testsCountAnalytics.ethnicities
      ? (Object.keys(analytics.testsCountAnalytics.ethnicities)
          .map((key) => getChartLabel(key))
          .slice(1) as string[])
      : [],
    datasets: [
      {
        label: 'Rainfall',
        backgroundColor: CHART_COLORS,
        data: analytics?.testsCountAnalytics.ethnicities
          ? Object.values(analytics.testsCountAnalytics.ethnicities)
              .map((key) => (key ? String(key) : '0'))
              .slice(1)
          : [],
      },
    ],
  }

  const raceChartData = {
    labels: analytics?.testsCountAnalytics.races
      ? (Object.keys(analytics.testsCountAnalytics.races)
          .map((key) => getChartLabel(key))
          .slice(1) as string[])
      : [],
    datasets: [
      {
        label: 'Rainfall',
        backgroundColor: CHART_COLORS,
        data: analytics?.testsCountAnalytics.races
          ? Object.values(analytics.testsCountAnalytics.races)
              .map((key) => (key ? String(key) : '0'))
              .slice(1)
          : [],
      },
    ],
  }

  const ageRangeData = analytics?.testsCountAnalytics?.ageRange

  const ageRangeChartData = {
    labels: ['<18', '18-27', '28-37', '38-57', '>58'],
    datasets: [
      {
        label: 'Rainfall',
        backgroundColor: CHART_COLORS,
        data: [
          ageRangeData?.under18,
          ageRangeData?.between18and27,
          ageRangeData?.between28and37,
          ageRangeData?.between38and57,
          ageRangeData?.over58,
        ],
      },
    ],
  }

  const Chart = (props: {
    data: {
      labels: string[]
      datasets: { label: string; backgroundColor: string[]; data: (string | null | undefined)[] }[]
    }
    title: string
  }) => {
    const { data, title } = props
    return (
      <Pie
        data={data}
        options={{
          plugins: {
            title: {
              display: true,
              text: title,
              font: {
                size: 24,
              },
            },
            legend: {
              display: true,
              position: 'right',
              labels: {
                font: {
                  size: 16,
                },
              },
            },
          },
        }}
      />
    )
  }

  let resultsChart: {
    labels: (string | null)[]
    positives: (number | null)[]
    negatives: (number | null)[]
  } = {
    labels: [],
    positives: [],
    negatives: [],
  }

  resultsChart = {
    ...resultsChart,
    labels: _.union(
      analytics?.testsCountAnalytics.positiveResultsChartLabel,
      analytics?.testsCountAnalytics.negativeResultsChartLabel
    ).sort((a, b) => new Date(a as string).valueOf() - new Date(b as string).valueOf()),
  }

  resultsChart.labels.forEach((element, index) => {
    if (analytics?.testsCountAnalytics.positiveResultsChartLabel?.includes(element))
      resultsChart.positives.push(
        analytics.testsCountAnalytics.positiveResultsChartValues![
          analytics?.testsCountAnalytics.positiveResultsChartLabel?.indexOf(element)
        ]
      )
    else if (index === 0 || index === resultsChart.labels.length - 1) {
      resultsChart.positives.push(0)
    } else {
      resultsChart.positives.push(null)
    }
    if (analytics?.testsCountAnalytics.negativeResultsChartLabel?.includes(element))
      resultsChart.negatives.push(
        analytics.testsCountAnalytics.negativeResultsChartValues![
          analytics?.testsCountAnalytics.negativeResultsChartLabel?.indexOf(element)
        ]
      )
    else if (index === 0 || index === resultsChart.labels.length - 1) {
      resultsChart.positives.push(0)
    } else {
      resultsChart.negatives.push(null)
    }
  })

  const stateLine = {
    labels: _.union(
      analytics?.testsCountAnalytics.positiveResultsChartLabel,
      analytics?.testsCountAnalytics.negativeResultsChartLabel
    ).sort((a, b) => new Date(a as string).valueOf() - new Date(b as string).valueOf()),
    datasets: [
      {
        label: 'Positives',
        fill: false,
        lineTension: 0.5,
        backgroundColor: 'rgba(92,7,29,1)',
        borderColor: 'rgba(92,7,29,1)',
        borderWidth: 2,
        data: resultsChart.positives,
      },
      {
        label: 'Negatives',
        fill: false,
        lineTension: 0.5,
        backgroundColor: 'rgba(75,192,192,1)',
        borderColor: 'rgba(75,192,192,1)',
        borderWidth: 2,
        data: resultsChart.negatives,
      },
    ],
  }

  return (
    <DashboardPage>
      <Container>
        <Card
          height="100%"
          p="1rem"
          justifyContent="space-between"
          alignItems="center"
          display="flex"
          flexWrap="wrap"
        >
          <Box width="100%">
            <Box alignItems="center" justifyContent="space-between" display="flex">
              <DateInput
                pr="1rem"
                id="dateFrom"
                name={FormName.DATE_FROM}
                label="Date From"
                value={
                  searchState.data.dateFrom
                    ? new Date(searchState.data[FormName.DATE_FROM])
                    : lastWeek
                }
                onChange={(date) => {
                  searchState.setSearchState(
                    {
                      [FormName.DATE_FROM]: date ? getFormattedDateAndTime(date) : null,
                      page: 1,
                    },
                    false,
                    true
                  )
                }}
                isFullWidth
                hasBottomContent
              />
              <DateInput
                pr="1rem"
                id="dateTo"
                minDate={new Date(dateFrom)}
                name={FormName.DATE_TO}
                label="Date To"
                value={
                  searchState.data.dateTo ? new Date(searchState.data[FormName.DATE_TO]) : today
                }
                onChange={(date) => {
                  searchState.setSearchState(
                    {
                      [FormName.DATE_TO]: date ? getFormattedDateAndTime(date) : null,
                      page: 1,
                    },
                    false,
                    true
                  )
                }}
                isFullWidth
                hasBottomContent
              />
              <SelectInput
                label="Testing Type"
                name={FormName.TYPE_FILTER}
                value={searchState.data.typeFilter}
                onChange={(e) => {
                  searchState.setSearchState(
                    {
                      [FormName.TYPE_FILTER]: e.target.value || null,
                      page: 1,
                    },
                    false,
                    true
                  )
                }}
                options={TYPE_FILTER_OPTIONS}
                width="10rem"
              />
              <StatesDropdown
                value={searchState.data.state}
                onChange={(value) => {
                  searchState.setSearchState(
                    {
                      [FormName.STATE]: value || null,
                      page: 1,
                    },
                    false,
                    true
                  )
                }}
                hasBottomContent
              />
              <Button onClick={handleFilter} size="medium">
                Filter
              </Button>
            </Box>
            <Box>
              <Box>
                <Text fontSize={fontSizes.l}>
                  {format(searchDates.from, 'PPP')} - {format(searchDates.to, 'PPP')}
                </Text>
              </Box>
              <Box width={isMobile ? '100%' : undefined}>
                <Label size="large" htmlFor={uuid}>
                  Amount of Results: {analytics?.testsCountAnalytics.total}
                </Label>
              </Box>
            </Box>
          </Box>
        </Card>

        {!isLoadingAnalytics ? (
          <>
            <Box
              height="100%"
              alignItems="center"
              marginTop={pxToRem(20)}
              flexWrap="wrap"
              gridGap="2rem"
            >
              <Box
                height="100%"
                alignItems="center"
                display="flex"
                marginTop={pxToRem(20)}
                flexWrap="wrap"
                justifyContent="space-between"
              >
                <Card minWidth={isMobile ? '100%' : '16%'}>
                  <Card.Title>Negatives</Card.Title>
                  <Card.ContentCenter>
                    <Text fontSize="3rem">{analytics?.testsCountAnalytics.negatives}</Text>
                  </Card.ContentCenter>
                </Card>

                <Card minWidth={isMobile ? '100%' : '16%'}>
                  <Card.Title>Positives</Card.Title>
                  <Card.ContentCenter>
                    <Text fontSize="3rem">{analytics?.testsCountAnalytics.positives}</Text>
                  </Card.ContentCenter>
                </Card>

                <Card minWidth={isMobile ? '100%' : '16%'}>
                  <Card.Title>Inconclusives</Card.Title>
                  <Card.ContentCenter>
                    <Text fontSize="3rem">{analytics?.testsCountAnalytics.inconclusive}</Text>
                  </Card.ContentCenter>
                </Card>

                <Card minWidth={isMobile ? '100%' : '16%'}>
                  <Card.Title>Unsatisfactory</Card.Title>
                  <Card.ContentCenter>
                    <Text fontSize="3rem">{analytics?.testsCountAnalytics.unsatisfactory}</Text>
                  </Card.ContentCenter>
                </Card>

                <Card minWidth={isMobile ? '100%' : '16%'}>
                  <Card.Title>Invalids</Card.Title>
                  <Card.ContentCenter>
                    <Text fontSize="3rem">{analytics?.testsCountAnalytics.invalid}</Text>
                  </Card.ContentCenter>
                </Card>
                <Card minWidth={isMobile ? '100%' : '16%'}>
                  <Card.Title>Pending</Card.Title>
                  <Card.ContentCenter>
                    <Text fontSize="3rem">{analytics?.testsCountAnalytics.pendings}</Text>
                  </Card.ContentCenter>
                </Card>
              </Box>
              <Box
                height="100%"
                alignItems="center"
                display="flex"
                marginTop={pxToRem(20)}
                flexWrap="wrap"
                justifyContent="space-between"
              >
                <Card minWidth={isMobile ? '100%' : '24%'} marginBottom={pxToRem(20)}>
                  <Card.Title>RT-PCR</Card.Title>
                  <Card.ContentCenter>
                    <Text fontSize="3rem">{analytics?.testsCountAnalytics.rtpcr}</Text>
                  </Card.ContentCenter>
                </Card>

                <Card minWidth={isMobile ? '100%' : '24%'} marginBottom={pxToRem(20)}>
                  <Card.Title>Rapid</Card.Title>
                  <Card.ContentCenter>
                    <Text fontSize="3rem">{analytics?.testsCountAnalytics.rapids}</Text>
                  </Card.ContentCenter>
                </Card>

                <Card minWidth={isMobile ? '100%' : '24%'} marginBottom={pxToRem(20)}>
                  <Card.Title>Antigen</Card.Title>
                  <Card.ContentCenter>
                    <Text fontSize="3rem">{analytics?.testsCountAnalytics.antigen}</Text>
                  </Card.ContentCenter>
                </Card>

                <Card minWidth={isMobile ? '100%' : '24%'} marginBottom={pxToRem(20)}>
                  <Card.Title>Strep</Card.Title>
                  <Card.ContentCenter>
                    <Text fontSize="3rem">{analytics?.testsCountAnalytics.strep}</Text>
                  </Card.ContentCenter>
                </Card>

                <Card minWidth={isMobile ? '100%' : '24%'} marginBottom={pxToRem(20)}>
                  <Card.Title>Influenza</Card.Title>
                  <Card.ContentCenter>
                    <Text fontSize="3rem">{analytics?.testsCountAnalytics.influenza}</Text>
                  </Card.ContentCenter>
                </Card>

                <Card minWidth={isMobile ? '100%' : '24%'} marginBottom={pxToRem(20)}>
                  <Card.Title>Lucira</Card.Title>
                  <Card.ContentCenter>
                    <Text fontSize="3rem">{analytics?.testsCountAnalytics.lucira}</Text>
                  </Card.ContentCenter>
                </Card>
                <Card minWidth={isMobile ? '100%' : '24%'} marginBottom={pxToRem(20)}>
                  <Card.Title>Sofia RSV</Card.Title>
                  <Card.ContentCenter>
                    <Text fontSize="3rem">{analytics?.testsCountAnalytics.sofiaRsv}</Text>
                  </Card.ContentCenter>
                </Card>
                <Card minWidth={isMobile ? '100%' : '24%'} marginBottom={pxToRem(20)}>
                  <Card.Title>Self Swab</Card.Title>
                  <Card.ContentCenter>
                    <Text fontSize="3rem">{analytics?.testsCountAnalytics.selfSwabTotal}</Text>
                  </Card.ContentCenter>
                </Card>
              </Box>
            </Box>
            <Box flexWrap="wrap" justifyContent="space-between" marginTop="1.2rem">
              <Card minWidth={isMobile ? '100%' : pxToRem(585)}>
                <Card.Content>
                  <Line
                    data={stateLine}
                    options={{
                      spanGaps: true,
                    }}
                  />
                </Card.Content>
              </Card>
            </Box>
            <Box display="flex" flexWrap="wrap" justifyContent="space-between">
              <Box display="flex" flexWrap="wrap" justifyContent="space-between" marginTop="1.2rem">
                <Card minWidth={isMobile ? '100%' : pxToRem(585)}>
                  <Card.Content>
                    <Chart data={genderChartData} title="Gender" />
                  </Card.Content>
                </Card>
              </Box>
              <Box display="flex" flexWrap="wrap" justifyContent="space-between" marginTop="1.2rem">
                <Card minWidth={isMobile ? '100%' : pxToRem(585)}>
                  <Card.Content>
                    <Chart data={ethnicityChartData} title="Ethnicity" />
                  </Card.Content>
                </Card>
              </Box>
            </Box>
            <Box display="flex" flexWrap="wrap" justifyContent="space-between">
              <Box display="flex" flexWrap="wrap" justifyContent="space-between" marginTop="1.2rem">
                <Card minWidth={isMobile ? '100%' : pxToRem(585)}>
                  <Card.Content>
                    <Chart data={ageRangeChartData} title="Age Range" />
                  </Card.Content>
                </Card>
              </Box>
              <Box display="flex" flexWrap="wrap" justifyContent="space-between" marginTop="1.2rem">
                <Card minWidth={isMobile ? '100%' : pxToRem(585)}>
                  <Card.Content>
                    <Chart data={raceChartData} title="Race" />
                  </Card.Content>
                </Card>
              </Box>
            </Box>
            <Box flexWrap="wrap" justifyContent="space-between" marginTop="1.2rem">
              <Card minWidth={isMobile ? '100%' : pxToRem(585)}>
                <Card.Title>States</Card.Title>
                <Card.Content>
                  <List verticalAlign="middle" huge selection size="large">
                    {analytics?.testsCountAnalytics.cities &&
                      Object.entries(analytics?.testsCountAnalytics.cities).map(
                        ([key, value]) =>
                          key &&
                          value && <AnalyticsList header={`${key}`} count={value as string} />
                      )}
                  </List>
                </Card.Content>
              </Card>
            </Box>
          </>
        ) : (
          <Card.Content>
            <Loading
              isLoading
              skeletonComp={<LoadingSkeleton rows={SKELETON_ROWS} columns={SKELETON_COLUMNS} />}
            />
          </Card.Content>
        )}
      </Container>
    </DashboardPage>
  )
}

export default Analytics
